// Copyright © 2025 The Things Industries B.V.

import React, { useCallback, useMemo } from 'react'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'

import Icon, { entityIcons } from '@ttn-lw/components/icon'

import FetchTable from '@ttn-lw/containers/fetch-table'

import DateTime from '@ttn-lw/lib/components/date-time'
import Message from '@ttn-lw/lib/components/message'

import Pill from '@console/components/pill/index.tti'

import sharedMessages from '@ttn-lw/lib/shared-messages'
import PropTypes from '@ttn-lw/lib/prop-types'

import {
  checkFromState,
  mayCreateApplications,
  mayCreateGateways,
} from '@console/lib/feature-checks'

import { getLabelEntities } from '@console/store/actions/labels.tti'

import {
  selectLabelById,
  selectLabeledEntities,
  selectLabeledTotalCount,
} from '@console/store/selectors/labels.tti'

const APPLICATIONS_TAB = 'applications'
const GATEWAYS_TAB = 'gateways'
const DEVICES_TAB = 'devices'
const ALL_TAB = 'all'

const tabs = [
  {
    title: sharedMessages.all,
    name: ALL_TAB,
  },
  {
    title: sharedMessages.applications,
    name: APPLICATIONS_TAB,
  },
  {
    title: sharedMessages.gateways,
    name: GATEWAYS_TAB,
  },
  {
    title: sharedMessages.devices,
    name: DEVICES_TAB,
  },
]

const getEntity = item =>
  Boolean(item.ids.application_id)
    ? 'application'
    : Boolean(item.ids.gateway_id)
      ? 'gateway'
      : 'device'

const LabelEntitiesList = ({ labelName, labelId }) => {
  const [tab, setTab] = React.useState(ALL_TAB)
  const isDevicesTab = tab === DEVICES_TAB
  const isAllTab = tab === ALL_TAB
  const label = useSelector(state => selectLabelById(state, labelId))

  const selectedLabel = (
    <Pill
      color={label.color}
      text={label.name || label.ids.label_id}
      className="p-cs-m fs-m br-xl3"
    />
  )

  const headers = [
    {
      name: 'name',
      displayName: sharedMessages.nameAndId,
      getValue: row => ({
        id: row.ids.application_id || row.ids.gateway_id || row.ids.device_id,
        name: row.name,
        appId: row.ids.application_ids?.application_id,
        type: Boolean(row.ids.application_id)
          ? 'APPLICATION'
          : Boolean(row.ids.gateway_id)
            ? 'GATEWAY'
            : 'END_DEVICE',
      }),
      render: ({ name, id, appId, type }) => (
        <div className="d-flex al-center gap-cs-xs">
          {Boolean(name) ? (
            <div className="d-flex al-center gap-cs-xs">
              <Icon icon={entityIcons[type]} />
              <div>
                <span className="p-0 fw-bold d-block">{name}</span>
                <span className="c-text-neutral-light d-block">
                  {appId ? `${appId}/${id}` : id}
                </span>
              </div>
            </div>
          ) : (
            <>
              <Icon icon={entityIcons[type]} />
              <span className="mt-0 p-0 fw-bold d-block">{appId ? `${appId}/${id}` : id}</span>
            </>
          )}
        </div>
      ),
    },
    {
      name: 'created_at',
      displayName: sharedMessages.createdAt,
      render: date => <DateTime.Relative value={date} />,
    },
  ]

  const mayCreateApps = useSelector(state => checkFromState(mayCreateApplications, state))
  const mayCreateGtws = useSelector(state => checkFromState(mayCreateGateways, state))
  const allBaseDataSelector = createSelector(
    selectLabeledEntities,
    selectLabeledTotalCount,
    (labeledEntities, totalCount) => ({
      labeledEntities,
      totalCount,
      mayAdd:
        tab === APPLICATIONS_TAB ? mayCreateApps : tab === GATEWAYS_TAB ? mayCreateGtws : false,
    }),
  )

  const getItemsAction = React.useCallback(
    filters => {
      const { tab, query } = filters
      setTab(tab)

      return getLabelEntities(label, { isSearch: query.length > 0, query, tab })
    },
    [label],
  )

  const getItemPathPrefix = useCallback(
    item => {
      const generalLink = `/${tab}/${item.ids?.application_id || item.ids.gateway_id || item.ids.device_id}`
      const allTabLink = `/${getEntity(item)}s/${item.ids.application_id || item.ids.gateway_id || item.ids.device_id}`
      const devicesTabLink = `/applications/${item.ids?.application_ids?.application_id}/devices/${item.ids.device_id}`
      const allTabDevicesLink = `/applications/${item.ids?.application_ids?.application_id}/devices/${item.ids.device_id}`

      return isAllTab
        ? getEntity(item) === 'device'
          ? allTabDevicesLink
          : allTabLink
        : isDevicesTab
          ? devicesTabLink
          : generalLink
    },
    [isAllTab, isDevicesTab, tab],
  )

  const searchPlaceholderMessage = useMemo(() => {
    switch (tab) {
      case APPLICATIONS_TAB:
        return sharedMessages.searchApplications
      case GATEWAYS_TAB:
        return sharedMessages.searchGateways
      case DEVICES_TAB:
        return sharedMessages.searchDevices
      default:
        return sharedMessages.searchEntities
    }
  }, [tab])

  const addLink = useMemo(() => {
    switch (tab) {
      case APPLICATIONS_TAB:
        return '/applications/add'
      case GATEWAYS_TAB:
        return '/gateways/add'
      default:
        return undefined
    }
  }, [tab])

  return (
    <FetchTable
      selectedItem={selectedLabel}
      noDivider
      addLink={addLink}
      backButtonLink="/labels"
      entity="labeledEntities"
      defaultOrder="-created_at"
      headers={headers}
      addMessage={
        tab === APPLICATIONS_TAB ? sharedMessages.addApplication : sharedMessages.registerGateway
      }
      baseDataSelector={allBaseDataSelector}
      getItemsAction={getItemsAction}
      tableTitle={<Message content={labelName} />}
      tabs={tabs}
      searchable
      searchPlaceholderMessage={searchPlaceholderMessage}
      handlesPagination
      getItemPathPrefix={getItemPathPrefix}
    />
  )
}

LabelEntitiesList.propTypes = {
  labelId: PropTypes.string.isRequired,
  labelName: PropTypes.string.isRequired,
}

export default LabelEntitiesList
